import { TITLE_FONT_SIZE } from './tutorialStyles'
import type { TutorialSlide } from '../screens/TutorialScreen'
import { NeonColors, FONTS } from '../neon-theme'
import { drawRightFinger } from './fingerHelper'

const SCALE = 2
const PARTICLE_COUNT = 80

// Timeline (cumulative seconds):
// 0–1:    map + button + text + finger starts entering
// 1–2:    finger slides to button
// 2–3:    finger presses button
// 3–4:    star rises from button to midpoint, button fades
// 4–4.5:  star shakes briefly + explodes
// 4.5–7:  text "1 ⭐ = 100 sparks", particles hover in place (hold on 100 sparks)
// 7–10:   text "ALL 100 scatter", particles fly to map
// 10–12:  hold (sparks on map)
// 12–13.5: fade out
// 13.5–15: fade in
const LOOP_DURATION = 15

interface Particle {
  startX: number; startZ: number
  endX: number; endZ: number
  size: number; landTime: number
  scatterAngle: number; scatterDist: number
}

export class EntryStakeSlide implements TutorialSlide {
  private container: HTMLDivElement | null = null
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private canvasW = 0; private canvasH = 0
  private animFrame = 0; private startTime = 0
  private particles: Particle[] = []
  private mounted = false

  mount(el: HTMLDivElement, _onReady: () => void): void {
    this.container = el
    el.style.padding = '0'
    el.style.justifyContent = 'flex-start'
    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText = 'position: absolute; inset: 0; width: 100%; height: 100%;'
    el.appendChild(this.canvas)
    this.mounted = true
    this.canvasW = 0; this.canvasH = 0
    this.animFrame = 0
    this.startTime = performance.now()
    this.particles = []
    this.tick()
  }

  unmount(): void {
    this.mounted = false
    if (this.animFrame) { cancelAnimationFrame(this.animFrame); this.animFrame = 0 }
    if (this.container) this.container.innerHTML = ''
    this.container = null; this.canvas = null; this.ctx = null
  }

  private resizeCanvas(): void {
    if (!this.canvas) return
    const rect = this.canvas.getBoundingClientRect()
    const w = Math.floor(rect.width) || 320
    const h = Math.floor(rect.height) || 500
    if (w === this.canvasW && h === this.canvasH) return
    this.canvasW = w; this.canvasH = h
    this.canvas.width = w * SCALE; this.canvas.height = h * SCALE
    this.ctx = this.canvas.getContext('2d')
  }

  private tick = (): void => {
    if (!this.mounted || !this.canvas) return
    this.resizeCanvas()
    if (!this.ctx || this.canvasW === 0) { this.animFrame = requestAnimationFrame(this.tick); return }
    const elapsed = (performance.now() - this.startTime) / 1000
    const t = elapsed % LOOP_DURATION
    // Clear particles during fade-in so they don't flash on screen
    if (t >= 13.5 || t < 0.05) this.particles = []
    this.draw(t)
    this.animFrame = requestAnimationFrame(this.tick)
  }

  private spawnParticles(starX: number, starZ: number, mapCX: number, mapCZ: number, mapR: number): void {
    this.particles = []
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + Math.random() * 0.3
      const scatterDist = 18 + Math.random() * 30
      const landR = (0.15 + Math.random() * 0.8) * mapR
      const landAngle = (i / PARTICLE_COUNT) * Math.PI * 2 + Math.random() * 0.5
      this.particles.push({
        startX: starX, startZ: starZ,
        endX: mapCX + Math.cos(landAngle) * landR,
        endZ: mapCZ + Math.sin(landAngle) * landR * 0.85,
        size: 1 + Math.random() * 1.2,
        landTime: 1.5 + Math.random() * 1.5,
        scatterAngle: angle, scatterDist,
      })
    }
  }

  private draw(t: number): void {
    const ctx = this.ctx!
    const w = this.canvasW * SCALE
    const h = this.canvasH * SCALE
    const s = SCALE

    const mapCX = w / 2
    const mapCZ = h * 0.30 + 20 * s
    const mapR = Math.min(w * 0.42, h * 0.22)
    const btnCX = w / 2
    const btnCZ = h * 0.58 + 40 * s
    const btnW = 180 * s
    const btnH = 48 * s
    const starOnBtnX = btnCX + 38 * s  // star icon position on button
    const starOnBtnZ = btnCZ
    const starMidZ = (mapCZ + mapR + btnCZ) / 2  // midpoint between map bottom edge and button
    const textCZ = btnCZ + 55 * s

    // Global fade
    const fadeOut = t >= 12 && t < 13.5
    const fadeIn = t >= 13.5
    const gf = fadeOut ? Math.max(0, 1 - (t - 12) / 1.5)
      : fadeIn ? Math.min(1, (t - 13.5) / 1.5)
        : 1

    ctx.clearRect(0, 0, w, h)
    ctx.fillStyle = NeonColors.bgDark
    ctx.fillRect(0, 0, w, h)

    // Title
    ctx.textAlign = 'center'; ctx.textBaseline = 'top'
    ctx.font = `bold ${TITLE_FONT_SIZE}px ${FONTS.title}`
    ctx.fillStyle = NeonColors.primary
    ctx.shadowColor = NeonColors.primaryGlow; ctx.shadowBlur = 12 * s
    ctx.globalAlpha = 0.95 * gf
    ctx.fillText('ENTRY STAKE', w / 2, 53 * s)
    ctx.shadowBlur = 0; ctx.globalAlpha = 1

    // ---- MAP (empty until sparks land) ----
    ctx.globalAlpha = gf * 0.7
    ctx.beginPath()
    ctx.arc(mapCX, mapCZ, mapR, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(255,255,255,0.03)'
    ctx.fill()
    ctx.strokeStyle = 'rgba(255,255,255,0.18)'
    ctx.lineWidth = 1.5 * s
    ctx.stroke()
    ctx.save()
    ctx.beginPath()
    ctx.arc(mapCX, mapCZ, mapR - 1, 0, Math.PI * 2)
    ctx.clip()
    ctx.strokeStyle = 'rgba(255,255,255,0.06)'; ctx.lineWidth = 1
    const gs = 25 * s
    for (let gx = mapCX - mapR; gx < mapCX + mapR; gx += gs) {
      ctx.beginPath(); ctx.moveTo(gx, mapCZ - mapR); ctx.lineTo(gx, mapCZ + mapR); ctx.stroke()
    }
    for (let gz = mapCZ - mapR; gz < mapCZ + mapR; gz += gs) {
      ctx.beginPath(); ctx.moveTo(mapCX - mapR, gz); ctx.lineTo(mapCX + mapR, gz); ctx.stroke()
    }
    ctx.restore()
    ctx.globalAlpha = 1

    // ---- PARTICLES ----
    if (this.particles.length > 0) {
      for (const p of this.particles) {
        let px: number, pz: number
        if (t < 7) {
          // Scatter outward from star (4.5 onward) and HOLD in place
          const scP = Math.min(1, Math.max(0, (t - 4.5) / 0.5))
          const ep = scP * scP * (3 - 2 * scP)
          px = p.startX + Math.cos(p.scatterAngle) * p.scatterDist * s * ep
          pz = p.startZ + Math.sin(p.scatterAngle) * p.scatterDist * s * ep
        } else if (t < 10) {
          // Fly to map
          const flyP = Math.min(1, (t - 7) / p.landTime)
          const ep = flyP * flyP * (3 - 2 * flyP)
          const scX = p.startX + Math.cos(p.scatterAngle) * p.scatterDist * s
          const scZ = p.startZ + Math.sin(p.scatterAngle) * p.scatterDist * s
          px = scX + (p.endX - scX) * ep
          pz = scZ + (p.endZ - scZ) * ep - Math.sin(ep * Math.PI) * 40 * s
        } else {
          px = p.endX; pz = p.endZ
        }
        ctx.beginPath()
        ctx.arc(px, pz, p.size * s, 0, Math.PI * 2)
        ctx.fillStyle = NeonColors.gold; ctx.globalAlpha = gf * 0.8
        ctx.shadowColor = NeonColors.gold; ctx.shadowBlur = p.size * s
        ctx.fill(); ctx.shadowBlur = 0
      }
      ctx.globalAlpha = 1
    }

    // ---- BUTTON ----
    const btnAlpha = t < 3 ? 1 : t < 4 ? Math.max(0, 1 - (t - 3)) : t >= 13.5 ? Math.min(1, (t - 13.5) / 1.5) : 0
    const btnPressed = t >= 2 && t < 3

    if (btnAlpha * gf > 0.01) {
      ctx.save()
      ctx.translate(btnCX, btnCZ)
      ctx.scale(btnPressed ? 0.94 : 1, btnPressed ? 0.94 : 1)
      ctx.globalAlpha = btnAlpha * gf

      ctx.beginPath()
      ctx.roundRect(-btnW / 2, -btnH / 2, btnW, btnH, 12 * s)
      ctx.strokeStyle = 'rgba(255,255,255,0.8)'
      ctx.lineWidth = 2 * s
      ctx.shadowColor = 'rgba(255,255,255,0.3)'
      ctx.shadowBlur = btnPressed ? 4 * s : 10 * s
      ctx.stroke(); ctx.shadowBlur = 0

      // Star icon on button (yellow)
      const starStillOnBtn = t < 3 || t >= 13.5
      if (starStillOnBtn) {
        ctx.save()
        ctx.translate(38 * s, 0)
        this.drawStar(ctx, 0, 0, 11.5 * s, 5.2 * s, 5)
        ctx.fillStyle = NeonColors.gold
        ctx.globalAlpha = 0.9
        ctx.fill()
        ctx.restore()
      }

      ctx.font = `bold ${Math.round(13 * 1.1 * s)}px ${FONTS.body}`
      ctx.fillStyle = '#FFFFFF'
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.globalAlpha = 1
      ctx.fillText('START', -5 * s, 0)

      ctx.restore()
      ctx.globalAlpha = 1
    }

    // ---- FLOATING STAR (3–4.5: rises, shakes briefly, explodes) ----
    if (t >= 3 && t < 5) {
      if (t < 4) {
        // Rising from button star position to midpoint
        const riseP = (t - 3) / 1
        const ep = riseP * riseP * (3 - 2 * riseP)
        const sx = starOnBtnX + (btnCX - starOnBtnX) * ep
        const sz = starOnBtnZ - ep * (starOnBtnZ - starMidZ)
        const grow = 1 + ep * 0.8
        ctx.save()
        ctx.translate(sx, sz)
        ctx.scale(grow, grow)
        ctx.globalAlpha = gf
        this.drawStar(ctx, 0, 0, 20 * s, 9 * s, 5)
        ctx.fillStyle = NeonColors.gold
        ctx.shadowColor = NeonColors.goldGlow; ctx.shadowBlur = 20 * s
        ctx.fill(); ctx.shadowBlur = 0
        ctx.restore()
      } else if (t < 4.3) {
        // Brief shake at midpoint (0.3s only)
        const shake = Math.sin(t * 80) * 5 * s
        const grow = 1.8 + (t - 4) * 1.0
        ctx.save()
        ctx.translate(btnCX + shake, starMidZ)
        ctx.scale(grow, grow)
        ctx.globalAlpha = gf
        this.drawStar(ctx, 0, 0, 20 * s, 9 * s, 5)
        ctx.fillStyle = NeonColors.gold
        ctx.shadowColor = NeonColors.goldGlow; ctx.shadowBlur = 20 * s
        ctx.fill(); ctx.shadowBlur = 0
        ctx.restore()
      } else {
        // Explosion (4.3–4.5) — no white flash, just spawn particles
        if (this.particles.length === 0) {
          this.spawnParticles(btnCX, starMidZ, mapCX, mapCZ, mapR)
        }
      }
    }

    // ---- FINGER (2–5.5: slides in toward star on button, presses) ----
    {
      const fingerTargetX = starOnBtnX + 5 * s  // aim at star on button, slightly right
      const fingerTargetZ = btnCZ + btnH * 0.65
      let fingerX = w + 30 * s
      let fingerZ = fingerTargetZ
      let fingerAlpha = 0
      let fingerScale = 1

      if (t >= 0 && t < 2) {
        const p = t / 2
        const ep = p * p * (3 - 2 * p)
        fingerX = w + 30 * s + (fingerTargetX - w - 30 * s) * ep
        fingerZ = fingerTargetZ
        fingerAlpha = Math.min(1, p * 1.5)
      } else if (t >= 2 && t < 3) {
        fingerX = fingerTargetX
        fingerZ = fingerTargetZ
        fingerAlpha = 1
        fingerScale = 0.82
      } else if (t >= 3 && t < 3.8) {
        fingerX = fingerTargetX
        fingerZ = fingerTargetZ
        fingerAlpha = Math.max(0, 1 - (t - 5) / 0.8)
      }

      if (fingerAlpha > 0.01) {
        drawRightFinger(ctx, fingerX, fingerZ, 44 * s, fingerAlpha * 0.75 * gf, fingerScale)
      }
    }

    // ---- TEXT ----
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'

    if (t < 3) {
      // "Every snake stakes 1 ⭐ to enter" — visible from first frame
      const a = 1
      ctx.font = `bold ${Math.round(10 * 1.1 * s)}px ${FONTS.body}`
      ctx.fillStyle = NeonColors.textBright; ctx.globalAlpha = a * 0.85 * gf
      ctx.fillText('Every snake stakes 1 ⭐ to enter', w / 2, textCZ)
    } else if (t >= 4.5 && t < 7) {
      // "1 ⭐ = 100 sparks" — hold while particles hover
      const a = Math.min(1, (t - 4.5) / 0.5)
      ctx.font = `bold ${Math.round(11 * 1.1 * s)}px ${FONTS.body}`
      ctx.fillStyle = NeonColors.textBright; ctx.globalAlpha = a * 0.9 * gf
      ctx.fillText('1 ⭐ = 100 sparks', w / 2, textCZ)
    } else if (t >= 7 && t < 12) {
      // Phase 7: "ALL 100 sparks scatter across the map"
      const a = Math.min(1, (t - 7) / 0.5)
      ctx.font = `${Math.round(10 * 1.1 * s)}px ${FONTS.body}`
      ctx.fillStyle = NeonColors.textMuted; ctx.globalAlpha = a * 0.85 * gf
      // Draw with ALL in caps/bold
      const textY = textCZ
      const allText = 'ALL'
      const restText = ' 100 sparks scatter across the map'
      ctx.font = `bold ${Math.round(11 * 1.1 * s)}px ${FONTS.body}`
      ctx.fillStyle = NeonColors.textBright
      const allWidth = ctx.measureText(allText).width
      ctx.font = `${Math.round(10 * 1.1 * s)}px ${FONTS.body}`
      ctx.fillStyle = NeonColors.textMuted
      const restWidth = ctx.measureText(restText).width
      const totalWidth = allWidth + restWidth
      const startX = w / 2 - totalWidth / 2
      // Draw ALL bold
      ctx.font = `bold ${Math.round(11 * 1.1 * s)}px ${FONTS.body}`
      ctx.fillStyle = NeonColors.textBright; ctx.globalAlpha = a * 0.95 * gf
      ctx.textAlign = 'left'
      ctx.fillText(allText, startX, textY)
      // Draw rest normal
      ctx.font = `${Math.round(10 * 1.1 * s)}px ${FONTS.body}`
      ctx.fillStyle = NeonColors.textMuted; ctx.globalAlpha = a * 0.75 * gf
      ctx.fillText(restText, startX + allWidth, textY)
      ctx.textAlign = 'center'
    }
    ctx.globalAlpha = 1

    // Fade overlay
    if (gf < 1) {
      ctx.fillStyle = NeonColors.bgDark
      ctx.globalAlpha = 1 - gf
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = 1
    }
  }

  private drawStar(ctx: CanvasRenderingContext2D, cx: number, cz: number, outerR: number, innerR: number, points: number): void {
    ctx.beginPath()
    for (let i = 0; i < points * 2; i++) {
      const angle = (i * Math.PI) / points - Math.PI / 2
      const r = i % 2 === 0 ? outerR : innerR
      ctx.lineTo(cx + Math.cos(angle) * r, cz + Math.sin(angle) * r)
    }
    ctx.closePath()
  }
}
